import { captureException } from './sentry';

/**
 * Predefined error messages used throughout the application.
 * These messages are used for consistent error handling and can be checked
 * to determine specific error types.
 */
export enum ErrorMessage {
  CHAT_DISABLED = 'Kustomer Chat is disabled in your org settings. Initialization will not continue.',
  /**
   * Logged when Kustomer `POST /c/v1/pubnub/auth` returns HTTP 400 (org without PubNub / bad token / config).
   * Paired with graceful skip in `initializePubNub` and Sentry `beforeSend` filtering (EUX-1702, EUX-1698).
   */
  PUBNUB_AUTH_NOT_CONFIGURED = 'PubNub auth is not available for this org (HTTP 400). Real-time chat features will be skipped.',
}

/**
 * Log labels used to categorize and prefix log messages.
 * Labels are displayed in brackets (e.g., [PubNubRecovery]) when used with
 * handleError or debug functions.
 */
export enum LogLabel {
  PUBNUB_RECOVERY = 'PubNubRecovery',
}

/** Sentry / console severity for `handleError`. Warnings do not page Escalating / High Error Rate. */
export type ErrorLevel = 'warning' | 'error';

/**
 * Properties that can be passed to error handling and logging functions.
 * All properties are optional, allowing for flexible usage.
 */
export interface ErrorProps {
  /** Optional error or log message */
  message?: string;
  /** Optional tags to add to Sentry context (for error tracking). Values can be boolean or string. */
  tags?: Record<string, boolean | string>;
  /** Optional array of labels to prefix the message with (e.g., [PubNubRecovery]) */
  labels?: string[];
  /**
   * Optional Sentry level (default `error`).
   * Use `warning` for expected / recoverable noise that should stay in Sentry without paging.
   */
  level?: ErrorLevel;
}

/**
 * Helper function to prefix a message with labels.
 * Labels are formatted as [Label1] [Label2] and prepended to the message.
 *
 * @param message - The message to prefix (optional)
 * @param labels - Array of labels to prefix the message with (optional)
 * @returns The message prefixed with labels in brackets, or just the message if no labels provided
 */
function prefixMessageWithLabels(
  message: string | undefined,
  labels: string[] | undefined,
): string | undefined {
  // No labels provided, return message as-is
  if (!labels || labels.length === 0) {
    return message;
  }
  // Format labels as [Label1] [Label2] and prepend to message
  const labelPrefix = labels.map((label) => `[${label}]`).join(' ');
  return message ? `${labelPrefix} ${message}` : labelPrefix;
}

/**
 * Handles errors by logging to console and reporting to Sentry.
 * Supports optional message, tags, labels, and level for enhanced error tracking.
 *
 * @param error - The error object to handle
 * @param options - Optional error properties
 * @param options.message - Optional error message (will be prefixed with labels if provided)
 * @param options.tags - Optional tags to add to Sentry context (merged with default errorBoundary: false tag)
 * @param options.labels - Optional array of labels to prefix the message with (e.g., [PubNubRecovery])
 * @param options.level - Optional Sentry/console level (`warning` | `error`, default `error`)
 * @returns The result of captureException, or undefined if error is CHAT_DISABLED
 */
export const handleError = (
  error,
  { message, tags, labels, level = 'error' }: ErrorProps = {},
) => {
  const prefixedMessage = prefixMessageWithLabels(message, labels);
  if (level === 'warning') {
    console.warn(prefixedMessage, error);
  } else {
    console.error(prefixedMessage, error);
  }

  // Skip Sentry reporting for chat disabled errors
  if (error.message === ErrorMessage.CHAT_DISABLED) {
    return undefined;
  }

  // Report to Sentry with tags and level
  return captureException(error, {
    level,
    tags: {
      errorBoundary: false,
      ...tags,
    },
  });
};

/**
 * Debug logging utility that only logs in non-production environments.
 * Messages can be prefixed with labels for better categorization.
 * This function is gated by NODE_ENV and will not log in production builds.
 *
 * @param options - Debug log options
 * @param options.message - The debug message to log (optional)
 * @param options.labels - Optional array of labels to prefix the message with (e.g., [PubNubRecovery])
 */
export const debug = ({ message, labels }: ErrorProps = {}) => {
  // Only log in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    let prefixedMessage = prefixMessageWithLabels(message, labels);
    // Don't log if there's no message and no labels
    if (prefixedMessage === undefined) {
      return;
    }
    if (prefixedMessage !== message) {
      // Add timestamp to the message for better visibility and debugging
      const timestamp = new Date().toISOString();
      prefixedMessage = `${prefixedMessage} ${timestamp}`;
    }
    // Use console.log instead of console.info for better visibility in browser dev tools
    console.log(prefixedMessage);
  }
};
